import axios, { AxiosInstance } from "axios";
import type { Feature, FeatureCollection, Point } from "geojson";
import { getIdToken } from "./firebase";
import { getConsent } from "./storage";

// Requests to /api/* are proxied to the backend:
// - In dev: Vite dev server proxies to http://backend:8000
// - In production: nginx proxies to the backend service URL
const API_URL = "/api/v1";

/**
 * Create axios instance with base configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Add auth token and consent header to requests
 */
apiClient.interceptors.request.use(
  async (config) => {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["X-Analytics-Consent"] = getConsent();
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Send magic link to email
 */
export async function sendMagicLink(
  email: string,
  redirectUrl: string,
): Promise<{ message: string; signInLink?: string }> {
  const response = await apiClient.post("/auth/send-magic-link", {
    email,
    redirectUrl,
  });
  return response.data.data;
}

/**
 * Get current user info
 */
export async function getCurrentUserInfo() {
  const response = await apiClient.get("/auth/me");
  return response.data.data;
}

// Landing Page API
export type PlaceType =
  | "restaurant"
  | "cafe"
  | "grocery-store"
  | "hiking"
  | "accommodation"
  | "backcountry"
  | "bar"
  | "coworking-space"
  | "gym"
  | "bike-park"
  | "ski-resort"
  | "convenience-store"
  | "pharmacy"
  | "public-transport"
  | "spa"
  | "swimming-pool"
  | "yoga-center"
  | "massage"
  | "department-store"
  | "public-park"
  | "library"
  | "fresh-market"
  | "parking"
  | "hut"
  | "hot-spring"
  | "mtb-trail"
  | "bike-shop"
  | "activity"
  | "campground"
  | "ski-board-shop";

export type PlaceSource =
  | "map_click"
  | "mapbox_poi"
  | "noma_place"
  | "search_mapbox"
  | "search_noma"
  | "inline_related";

export interface SectionConfig {
  description?: string;
}

export interface LandingPageConfig {
  sections?: Partial<Record<PlaceType, SectionConfig>>;
}

export interface LandingPageLogo {
  id: string;
  original: string;
  thumb: string;
}

// Maps API
export type MapRole = "owner" | "editor";

export interface MapPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canTransfer: boolean;
  canPublishLandingPage: boolean;
  canCreateShareLink: boolean;
  canShare: boolean;
  canManageCollaborators: boolean;
}

export interface AdventureMap {
  id: string;
  shareId: string | null;
  userId: string;
  name: string;
  description: string | null;
  landingPage: {
    slug: string | null;
    publishedAt: Date | null;
  } | null;
  role: MapRole | null;
  permissions: MapPermissions;
  curatedBy?: { displayName: string } | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

interface AdventureMapRaw {
  id: string;
  shareId: string | null;
  userId: string;
  name: string;
  description: string | null;
  landingPage: {
    slug: string | null;
    publishedAt: string | null;
  } | null;
  role: MapRole | null;
  permissions: MapPermissions;
  curatedBy?: { displayName: string } | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

function parseDate(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

function transformMap(raw: AdventureMapRaw): AdventureMap {
  return {
    id: raw.id,
    shareId: raw.shareId,
    userId: raw.userId,
    name: raw.name,
    description: raw.description,
    landingPage: raw.landingPage
      ? {
          slug: raw.landingPage.slug,
          publishedAt: parseDate(raw.landingPage.publishedAt),
        }
      : null,
    role: raw.role,
    permissions: raw.permissions,
    curatedBy: raw.curatedBy,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    deletedAt: parseDate(raw.deletedAt),
  };
}

export interface CreateMapData {
  name: string;
  description?: string;
}

export interface UpdateMapData {
  name?: string;
  description?: string | null;
}

/**
 * Create a new adventure map
 */
export async function createMap(data: CreateMapData): Promise<AdventureMap> {
  const response = await apiClient.post("/studio/maps", data);
  return transformMap(response.data.data);
}

/**
 * Get all maps for the current user
 */
export async function getUserMaps(): Promise<AdventureMap[]> {
  const response = await apiClient.get("/studio/maps");
  return response.data.data.map(transformMap);
}

/**
 * Get a map by ID
 */
export async function getMapById(mapId: string): Promise<AdventureMap> {
  const response = await apiClient.get(`/studio/maps/${mapId}`);
  return transformMap(response.data.data);
}

/**
 * Update a map
 */
export async function updateMap(mapId: string, data: UpdateMapData): Promise<AdventureMap> {
  const response = await apiClient.put(`/studio/maps/${mapId}`, data);
  return transformMap(response.data.data);
}

/**
 * Delete a map (soft delete)
 */
export async function deleteMap(mapId: string): Promise<void> {
  await apiClient.delete(`/studio/maps/${mapId}`);
}

export async function transferMapOwnership(mapId: string, email: string): Promise<void> {
  await apiClient.post(`/studio/maps/${mapId}/transfer`, { email });
}

export async function transferPlaceOwnership(placeId: string, email: string): Promise<void> {
  await apiClient.post(`/studio/places/${placeId}/transfer`, { email });
}

/**
 * Share a map - generates a shareId if one doesn't exist
 */
export async function shareMap(mapId: string): Promise<AdventureMap> {
  const response = await apiClient.post(`/studio/maps/${mapId}/share`);
  return transformMap(response.data.data);
}

/**
 * Check if a slug is available for a map
 */
export async function checkSlugAvailability(
  mapId: string,
  slug: string,
): Promise<{ available: boolean; reason?: string }> {
  const response = await apiClient.get(`/studio/maps/${mapId}/slug-availability/${slug}`);
  return response.data.data;
}

export interface LandingPage {
  id: string;
  mapId: string;
  slug: string | null;
  config: LandingPageConfig | null;
  logo: LandingPageLogo | null;
  publishedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

interface LandingPageRaw {
  id: string;
  mapId: string;
  slug: string | null;
  config: LandingPageConfig | null;
  logo: LandingPageLogo | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function transformLandingPage(raw: LandingPageRaw): LandingPage {
  return {
    ...raw,
    publishedAt: parseDate(raw.publishedAt),
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

/**
 * Get landing page for a map. Returns null if no landing page exists.
 */
export async function getLandingPage(mapId: string): Promise<LandingPage | null> {
  const response = await apiClient.get(`/studio/maps/${mapId}/landing-page`);
  const data = response.data.data;
  return data ? transformLandingPage(data) : null;
}

/**
 * Update landing page configuration.
 *
 * @param data.logoId - UUID to set, empty string to remove, undefined for no change
 */
export async function updateLandingPage(
  mapId: string,
  data: {
    config: LandingPageConfig;
    name?: string;
    description?: string;
    slug?: string;
    publish?: boolean;
    logoId?: string;
  },
): Promise<LandingPage> {
  const response = await apiClient.put(`/studio/maps/${mapId}/landing-page`, data);
  return transformLandingPage(response.data.data);
}

/**
 * Upload a logo for landing page (stores file, does not attach until save)
 */
export async function uploadLandingPageLogo(mapId: string, file: File): Promise<LandingPageLogo> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await apiClient.post(`/studio/maps/${mapId}/landing-page/logo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.data;
}

// Places API
export const PLACE_TYPE_META: Record<PlaceType, { label: string; labelPlural: string }> = {
  // Activities
  hiking: { label: "Hiking Trail", labelPlural: "Hiking Trails" },
  "mtb-trail": { label: "MTB Trail", labelPlural: "MTB Trails" },
  "bike-park": { label: "Bike Park", labelPlural: "Bike Parks" },
  "bike-shop": { label: "Bike Shop", labelPlural: "Bike Shops" },
  "ski-board-shop": { label: "Ski & Board Shop", labelPlural: "Ski & Board Shops" },
  backcountry: {
    label: "Backcountry Route",
    labelPlural: "Backcountry Routes",
  },
  "ski-resort": { label: "Ski Resort", labelPlural: "Ski Resorts" },
  // Food & Drink
  restaurant: { label: "Restaurant", labelPlural: "Restaurants" },
  cafe: { label: "Coffee Shop", labelPlural: "Coffee Shops" },
  bar: { label: "Bar", labelPlural: "Bars" },
  // Accommodation
  accommodation: { label: "Accommodation", labelPlural: "Accommodations" },
  hut: { label: "Hut", labelPlural: "Huts" },
  campground: { label: "Campground", labelPlural: "Campgrounds" },
  // Shopping & Essentials
  "grocery-store": { label: "Grocery", labelPlural: "Groceries" },
  "fresh-market": { label: "Market", labelPlural: "Markets" },
  "convenience-store": {
    label: "Convenience Store",
    labelPlural: "Convenience Stores",
  },
  "department-store": {
    label: "Department Store",
    labelPlural: "Department Stores",
  },
  pharmacy: { label: "Pharmacy", labelPlural: "Pharmacies" },
  // Wellness & Fitness
  gym: { label: "Gym", labelPlural: "Gyms" },
  spa: { label: "Spa", labelPlural: "Spas" },
  "hot-spring": { label: "Hot Spring", labelPlural: "Hot Springs" },
  "yoga-center": { label: "Yoga Center", labelPlural: "Yoga Centers" },
  massage: { label: "Massage", labelPlural: "Massage" },
  "swimming-pool": { label: "Pool", labelPlural: "Pools" },
  // Services & Infrastructure
  "coworking-space": { label: "Coworking", labelPlural: "Coworking Spaces" },
  library: { label: "Library", labelPlural: "Libraries" },
  "public-park": { label: "Public Park", labelPlural: "Public Parks" },
  "public-transport": { label: "Transit", labelPlural: "Transit" },
  parking: { label: "Parking", labelPlural: "Parking" },
  // Catch-all
  activity: { label: "Other Activity", labelPlural: "Other Activities" },
};

// Image with all variant URLs (returned by backend)
export interface PlaceImage {
  id: string;
  original: string;
  large: string;
  medium: string;
  small: string;
  thumb: string;
  srcset: string;
}

// Base place - common fields for all place types
interface BasePlace {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  description: string | null;
  links: string[];
  images: PlaceImage[];
  email: string | null;
  phone: string | null;
}

// Hiking trails
export type HikingDifficulty = "easy" | "moderate" | "hard" | "expert";
export type RouteType = "loop" | "out-and-back" | "point-to-point";

export interface HikingPlace extends BasePlace {
  type: "hiking";
  difficulty: HikingDifficulty | null;
  distance: { value: number; unit: "km" | "mi" } | null;
  elevation: { gain: number; loss: number; unit: "m" | "ft" } | null;
  routeType: RouteType | null;
  surface: string[];
  features: string[];
}

// MTB trails
export type MtbTrailDifficulty = "green" | "blue" | "black" | "double-black" | "proline";
export type MtbTrailCategory = "xc" | "enduro" | "downhill" | "freeride" | "dirt-jump";
export type MtbTrailCharacter = "flow" | "tech" | "jumps" | "drops" | "steep" | "rocky" | "rooty" | "loose";
export type MtbTrailFeature = "rock-gardens" | "berms" | "rollers" | "gaps" | "skinnies" | "northshore" | "switchbacks";
export type MtbTrailBuild = "old-school" | "hand-built" | "machine-built";

export interface MtbTrailMetadata {
  difficulty?: MtbTrailDifficulty;
  category?: MtbTrailCategory;
  character?: MtbTrailCharacter[];
  features?: MtbTrailFeature[];
  build?: MtbTrailBuild;
  distance?: { value: number; unit: "km" | "mi" };
  elevation?: { gain: number; loss: number; unit: "m" | "ft" };
}

// Bike parks
export interface BikeParkPlace extends BasePlace {
  type: "bike-park";
  liftAccess: boolean;
  shuttleAccess: boolean;
}

// Ski resorts
export type SkiTrailDifficulty = "green" | "blue" | "black" | "double-black" | "park";
export type SkiTrailCategory = "groomed" | "natural";

export interface SkiTrail {
  name: string;
  difficulty: SkiTrailDifficulty;
  category: SkiTrailCategory;
}

export interface SkiResortPlace extends BasePlace {
  type: "ski-resort";
  trails: SkiTrail[];
  lifts: number | null;
  vertical: { value: number; unit: "m" | "ft" } | null;
  summit: { value: number; unit: "m" | "ft" } | null;
  base: { value: number; unit: "m" | "ft" } | null;
  snowmaking: boolean;
  nightSkiing: boolean;
  season: { open: number; close: number } | null;
  passes: string[];
}

// Generic place - all other types
export interface GenericPlace extends BasePlace {
  type: Exclude<PlaceType, "hiking" | "bike-park" | "ski-resort">;
}

// Discriminated union
export type Place = HikingPlace | BikeParkPlace | SkiResortPlace | GenericPlace;

// Type guards
export function isHikingPlace(place: Place): place is HikingPlace {
  return place.type === "hiking";
}

export function isBikeParkPlace(place: Place): place is BikeParkPlace {
  return place.type === "bike-park";
}

export function isSkiResortPlace(place: Place): place is SkiResortPlace {
  return place.type === "ski-resort";
}

// Typed metadata for GeoJSON PlaceProperties
export interface HikingMetadata {
  difficulty?: HikingDifficulty;
  distance?: { value: number; unit: "km" | "mi" };
  elevation?: { gain: number; loss: number; unit: "m" | "ft" };
  routeType?: RouteType;
  surface?: string[];
  features?: string[];
}

export type Aspect = "N" | "NE" | "E" | "SE" | "S" | "SW" | "W" | "NW";
export const ASPECTS: Aspect[] = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

export interface BackcountryMetadata {
  difficulty?: HikingDifficulty;
  distance?: { value: number; unit: "km" | "mi" };
  elevation?: { gain: number; loss: number; unit: "m" | "ft" };
  duration?: { min: number; max: number; unit: "hours" };
  aspect?: Aspect[];
  bestMonths?: number[];
}

export interface BikeParkMetadata {
  liftAccess?: boolean;
  shuttleAccess?: boolean;
}

export type BedType = "single" | "double" | "queen" | "king" | "bunk" | "sofa-bed" | "bassinet";

export const AMENITIES = [
  "bike-storage",
  "kitchen",
  "fully-furnished-kitchen",
  "washer-dryer",
  "washer-drying-rack",
  "free-parking-premises",
  "paid-parking-premises",
  "air-conditioner",
  "towels-soap",
  "television",
  "elevator",
  "smart-lock",
  "outdoor-pool",
  "fireplace",
] as const;

export type Amenity = (typeof AMENITIES)[number];

export type WifiSpeed = "slow" | "regular" | "fast" | "very-fast";
export type WifiStability = "unstable" | "stable" | "flawless";

export interface AccommodationMetadata {
  guests?: number;
  bedrooms?: number;
  beds?: Partial<Record<BedType, number>>;
  bathrooms?: number;
  amenities?: Amenity[];
  workspaces?: number;
  wifi?: {
    speed?: WifiSpeed;
    stability?: WifiStability;
  };
  petsAllowed?: boolean;
  childrenAllowed?: boolean;
}

export type PlaceMetadata =
  | HikingMetadata
  | BackcountryMetadata
  | MtbTrailMetadata
  | BikeParkMetadata
  | AccommodationMetadata;

// Place types with specialized metadata
type PlaceTypeWithMetadata = "hiking" | "backcountry" | "mtb-trail" | "bike-park" | "accommodation";

export type PlaceStatus = "unlisted" | "published";

export interface PlacePermissions {
  canEdit: boolean;
  canDelete: boolean;
  canPublish: boolean;
  canTransfer: boolean;
}

interface BaseRelatedPlace {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  images: PlaceImage[];
}

export interface HikingRelatedPlace extends BaseRelatedPlace {
  type: "hiking";
  metadata?: HikingMetadata;
}

export interface BackcountryRelatedPlace extends BaseRelatedPlace {
  type: "backcountry";
  metadata?: BackcountryMetadata;
}

export interface MtbTrailRelatedPlace extends BaseRelatedPlace {
  type: "mtb-trail";
  metadata?: MtbTrailMetadata;
}

export interface BikeParkRelatedPlace extends BaseRelatedPlace {
  type: "bike-park";
  metadata?: BikeParkMetadata;
}

interface GenericRelatedPlace extends BaseRelatedPlace {
  type: Exclude<PlaceType, PlaceTypeWithMetadata>;
  metadata?: PlaceMetadata;
}

export type RelatedPlace =
  | HikingRelatedPlace
  | BackcountryRelatedPlace
  | MtbTrailRelatedPlace
  | BikeParkRelatedPlace
  | GenericRelatedPlace;

export interface PricePoint {
  type: string;
  label?: string;
  amount: number;
}

/** Marks a place as free to access. */
export interface FreePricing {
  free: true;
}

/** One or more price points in a single currency. */
export interface PricedPricing {
  free?: never;
  currency: string;
  prices: PricePoint[];
}

/**
 * A place's pricing. Narrow on `pricing.free` to discriminate:
 * `if (pricing.free)` → `FreePricing`, otherwise `PricedPricing`.
 */
export type Pricing = FreePricing | PricedPricing;

/** Base properties shared by all place types in GeoJSON format. */
interface BasePlaceProperties {
  id: string;
  status?: PlaceStatus;
  permissions?: PlacePermissions;
  name: string;
  description: string | null;
  links: string[];
  images: PlaceImage[];
  email: string | null;
  phone: string | null;
  pricing?: Pricing | null;

  relatedPlaces?: RelatedPlace[];
}

export interface HikingPlaceProperties extends BasePlaceProperties {
  type: "hiking";
  metadata: HikingMetadata | null;
  track?: GeoJSON.LineString | null;
}

export interface BackcountryPlaceProperties extends BasePlaceProperties {
  type: "backcountry";
  metadata: BackcountryMetadata | null;
  track?: GeoJSON.LineString | null;
}

export interface MtbTrailPlaceProperties extends BasePlaceProperties {
  type: "mtb-trail";
  metadata: MtbTrailMetadata | null;
  track?: GeoJSON.LineString | null;
}

export interface BikeParkPlaceProperties extends BasePlaceProperties {
  type: "bike-park";
  metadata: BikeParkMetadata | null;
}

export interface AccommodationPlaceProperties extends BasePlaceProperties {
  type: "accommodation";
  metadata: AccommodationMetadata | null;
}

export interface GenericPlaceProperties extends BasePlaceProperties {
  type: Exclude<PlaceType, PlaceTypeWithMetadata>;
  metadata: PlaceMetadata | null;
}

/** Discriminated union of all place property types */
export type PlaceProperties =
  | HikingPlaceProperties
  | BackcountryPlaceProperties
  | MtbTrailPlaceProperties
  | BikeParkPlaceProperties
  | AccommodationPlaceProperties
  | GenericPlaceProperties;

export type PlaceGeoJSON = FeatureCollection<Point, PlaceProperties> & {
  attribution?: string;
};
export type PlaceFeature = Feature<Point, PlaceProperties>;

/** Place properties for preview mode (POIs without detected type). Type can be null. */
export type PlacePreviewProperties = BasePlaceProperties & {
  type: PlaceType | null;
  metadata: PlaceMetadata | null;
};
export type PlacePreviewFeature = Feature<Point, PlacePreviewProperties>;

export function isHikingPlaceProperties(props: PlacePreviewProperties): props is HikingPlaceProperties {
  return props.type === "hiking";
}

export function isBackcountryPlaceProperties(props: PlacePreviewProperties): props is BackcountryPlaceProperties {
  return props.type === "backcountry";
}

export function isMtbTrailPlaceProperties(props: PlacePreviewProperties): props is MtbTrailPlaceProperties {
  return props.type === "mtb-trail";
}

export function isBikeParkPlaceProperties(props: PlacePreviewProperties): props is BikeParkPlaceProperties {
  return props.type === "bike-park";
}

export function isAccommodationPlaceProperties(props: PlacePreviewProperties): props is AccommodationPlaceProperties {
  return props.type === "accommodation";
}

export function isMtbTrailRelatedPlace(place: RelatedPlace): place is MtbTrailRelatedPlace {
  return place.type === "mtb-trail";
}

export function getPlaceTrack(props: PlacePreviewProperties | PlaceProperties): GeoJSON.LineString | null | undefined {
  if (isHikingPlaceProperties(props) || isBackcountryPlaceProperties(props) || isMtbTrailPlaceProperties(props)) {
    return props.track;
  }
  return undefined;
}

export function relatedPlaceToPreview(related: RelatedPlace): PlacePreviewFeature {
  return {
    type: "Feature",
    id: related.id,
    geometry: {
      type: "Point",
      coordinates: [related.longitude, related.latitude],
    },
    properties: {
      id: related.id,
      name: related.name,
      type: related.type,
      description: null,
      links: [],
      images: related.images,
      email: null,
      phone: null,
      metadata: null,
    },
  };
}

export interface CreatePlaceData {
  name: string;
  type: PlaceType;
  latitude: number;
  longitude: number;
  description?: string;
  links?: string[];
  images?: string[];
  email?: string;
  phone?: string;
  metadata?: PlaceMetadata;
  source?: PlaceSource;
}

export interface UpdatePlaceData {
  name?: string;
  type?: PlaceType;
  latitude?: number;
  longitude?: number;
  description?: string | null;
  links?: string[];
  images?: string[];
  email?: string | null;
  phone?: string | null;
  metadata?: PlaceMetadata | null;
  pricing?: Pricing | null;
}

/**
 * Get all places for an adventure map as GeoJSON
 */
export async function getGeoJsonPlacesByAdventureMapId(mapId: string): Promise<PlaceGeoJSON> {
  const response = await apiClient.get(`/studio/maps/${mapId}/places?format=geojson`);
  return response.data.data;
}

/**
 * Get all places for an adventure map as array
 */
export async function getPlacesByAdventureMapId(mapId: string): Promise<Place[]> {
  const response = await apiClient.get(`/studio/maps/${mapId}/places`);
  return response.data.data;
}

export async function createAndAddToMap(mapId: string, data: CreatePlaceData): Promise<Place> {
  const response = await apiClient.post(`/studio/maps/${mapId}/places`, data);
  return response.data.data;
}

export async function createPlace(data: CreatePlaceData): Promise<Place> {
  const response = await apiClient.post(`/studio/places`, data);
  return response.data.data;
}

/**
 * Update a place
 */
export async function updatePlace(placeId: string, data: UpdatePlaceData): Promise<Place> {
  const response = await apiClient.put(`/studio/places/${placeId}`, data);
  return response.data.data;
}

/**
 * Delete a place (soft delete)
 */
export async function deletePlace(mapId: string, placeId: string): Promise<void> {
  await apiClient.delete(`/studio/maps/${mapId}/places/${placeId}`);
}

/**
 * Publish a place to the global database
 */
export async function publishPlace(placeId: string): Promise<Place> {
  const response = await apiClient.post(`/studio/places/${placeId}/publish`);
  return response.data.data;
}

// Curator API
export interface Curator {
  id: string;
  userId: string | null;
  displayName: string;
  profileUrl: string | null;
  createdAt: string;
}

export type CuratorProfileTrigger = "share_map" | "publish_landing_page" | "publish_place";

export interface CreateCuratorData {
  displayName: string;
  profileUrl?: string;
  trigger?: CuratorProfileTrigger;
}

/**
 * Create curator profile for current user
 */
export async function createCuratorProfile(data: CreateCuratorData): Promise<Curator> {
  const response = await apiClient.post("/studio/profile", data);
  return response.data.data;
}

// Re-export error utilities from dedicated module
export { getApiErrorMessage, isAccessRevokedError, isCuratorProfileRequiredError, isNotFoundError } from "./api/errors";

export async function addPlaceToMap(mapId: string, placeId: string): Promise<void> {
  await apiClient.post(`/studio/maps/${mapId}/places`, { placeId });
}

/**
 * Get all browsable places for the current user as GeoJSON.
 * Returns published places + user's own unlisted places.
 */
export async function getStudioPlacesGeoJSON(): Promise<PlaceGeoJSON> {
  const response = await apiClient.get("/studio/places?format=geojson");
  return response.data.data;
}

// Public API Types

export interface SharedAdventureMap {
  shareId: string;
  name: string;
  description: string | null;
  landingPage: { slug: string; publishedAt: Date } | null;
  curatedBy: { displayName: string } | null;
  updatedAt: Date;
}

interface SharedAdventureMapRaw {
  shareId: string;
  name: string;
  description: string | null;
  landingPage: { slug: string; publishedAt: string } | null;
  curatedBy: { displayName: string } | null;
  updatedAt: string;
}

function transformSharedAdventureMap(raw: SharedAdventureMapRaw): SharedAdventureMap {
  return {
    ...raw,
    landingPage: raw.landingPage
      ? {
          slug: raw.landingPage.slug,
          publishedAt: new Date(raw.landingPage.publishedAt),
        }
      : null,
    updatedAt: new Date(raw.updatedAt),
  };
}

export interface PublishedLandingPage {
  slug: string;
  name: string;
  description: string | null;
  config: LandingPageConfig;
  logo: LandingPageLogo | null;
  publishedAt: Date;
  updatedAt: Date;
  curatedBy: { displayName: string } | null;
}

interface PublishedLandingPageRaw {
  slug: string;
  name: string;
  description: string | null;
  config: LandingPageConfig;
  logo: LandingPageLogo | null;
  publishedAt: string;
  updatedAt: string;
  curatedBy: { displayName: string } | null;
}

function transformPublishedLandingPage(raw: PublishedLandingPageRaw): PublishedLandingPage {
  return {
    ...raw,
    publishedAt: new Date(raw.publishedAt),
    updatedAt: new Date(raw.updatedAt),
  };
}

// Public API (no auth required)

export async function getMapByShareId(shareId: string): Promise<SharedAdventureMap> {
  const response = await apiClient.get(`/maps/${shareId}`);
  return transformSharedAdventureMap(response.data.data);
}

export async function getGeoJsonPlacesByShareId(shareId: string): Promise<PlaceGeoJSON> {
  const response = await apiClient.get(`/maps/${shareId}/places?format=geojson`);
  return response.data.data;
}

export async function getLandingPageBySlug(slug: string): Promise<PublishedLandingPage> {
  const response = await apiClient.get(`/landing-pages/${slug}`);
  return transformPublishedLandingPage(response.data.data);
}

export async function getGeoJsonPlacesByLandingPageSlug(slug: string): Promise<PlaceGeoJSON> {
  const response = await apiClient.get(`/landing-pages/${slug}/places?format=geojson`);
  return response.data.data;
}

// Image upload API

/**
 * Upload an image for a place
 */
export async function uploadPlaceImage(placeId: string, file: Blob): Promise<PlaceImage> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(`/studio/places/${placeId}/images`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.data;
}

/**
 * Delete an image from a place
 */
export async function deletePlaceImage(placeId: string, imageId: string): Promise<void> {
  await apiClient.delete(`/studio/places/${placeId}/images/${imageId}`);
}

export async function uploadPlaceTrack(placeId: string, file: Blob): Promise<GeoJSON.LineString> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiClient.post(`/studio/places/${placeId}/track`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.data;
}

export async function deletePlaceTrack(placeId: string): Promise<void> {
  await apiClient.delete(`/studio/places/${placeId}/track`);
}

// Related Places API

/**
 * Add a related place to a place within a map context
 */
export async function addRelatedPlace(mapId: string, placeId: string, relatedPlaceId: string): Promise<void> {
  await apiClient.post(`/studio/maps/${mapId}/places/${placeId}/related`, {
    relatedPlaceId,
  });
}

/**
 * Remove a related place from a place within a map context
 */
export async function removeRelatedPlace(mapId: string, placeId: string, relatedPlaceId: string): Promise<void> {
  await apiClient.delete(`/studio/maps/${mapId}/places/${placeId}/related/${relatedPlaceId}`);
}

// Collaborators API

export interface Collaborator {
  id: string;
  userId: string;
  email: string;
  role: string;
  addedAt: Date;
}

export interface PendingInvite {
  id: string;
  email: string;
  invitedAt: Date;
  expiresAt: Date;
}

interface CollaboratorRaw {
  id: string;
  userId: string;
  email: string;
  role: string;
  addedAt: string;
}

interface PendingInviteRaw {
  id: string;
  email: string;
  invitedAt: string;
  expiresAt: string;
}

function transformCollaborator(raw: CollaboratorRaw): Collaborator {
  return {
    ...raw,
    addedAt: new Date(raw.addedAt),
  };
}

function transformPendingInvite(raw: PendingInviteRaw): PendingInvite {
  return {
    ...raw,
    invitedAt: new Date(raw.invitedAt),
    expiresAt: new Date(raw.expiresAt),
  };
}

/**
 * Invite a collaborator by email
 */
export async function inviteCollaborator(mapId: string, email: string): Promise<PendingInvite> {
  const response = await apiClient.post(`/studio/maps/${mapId}/collaborators`, {
    email,
  });
  return transformPendingInvite(response.data.data);
}

/**
 * Get all collaborators for a map
 */
export async function getCollaborators(mapId: string): Promise<Collaborator[]> {
  const response = await apiClient.get(`/studio/maps/${mapId}/collaborators`);
  return response.data.data.map(transformCollaborator);
}

/**
 * Remove a collaborator from a map
 */
export async function removeCollaborator(mapId: string, collaboratorId: string): Promise<void> {
  await apiClient.delete(`/studio/maps/${mapId}/collaborators/${collaboratorId}`);
}

/**
 * Get pending invites for a map
 */
export async function getPendingInvites(mapId: string): Promise<PendingInvite[]> {
  const response = await apiClient.get(`/studio/maps/${mapId}/invites`);
  return response.data.data.map(transformPendingInvite);
}

/**
 * Cancel a pending invite
 */
export async function cancelInvite(mapId: string, inviteId: string): Promise<void> {
  await apiClient.delete(`/studio/maps/${mapId}/invites/${inviteId}`);
}

/**
 * Accept an invite by token
 */
export async function acceptInvite(token: string): Promise<{ mapId: string }> {
  const response = await apiClient.post(`/studio/invites/${token}/accept`);
  return response.data.data;
}

export default apiClient;
